import * as React from "react";
import { MarketplaceReleaseInstance, ReleaseRevision } from "../types/release";
import { Table } from "../../../newcomponents/table/table";
import { Button, Modal, notification } from "antd";
import { DateString } from "../../../components/dateString";
import { request } from "../../../services/cpln";
import { formatStrings } from "../../../utils/time";
import { CodeEditor } from "../../group/identityMatcher/codeEditor";
import { getMarketplaceErrorMessage } from "../utils";
import jsYaml from "js-yaml";
import { k8sKeySort } from "../../../services/utils";
import { useAppDispatch } from "../../../store/hooks";
import { addNotification } from "../../../store/notificationDrawerSlice";

interface ReleaseRevisionTableItem extends ReleaseRevision {
  isLoadingRollback: boolean;
  isLoadingRollbackThis: boolean;
}

interface Props {
  release: MarketplaceReleaseInstance;
  revisions: ReleaseRevision[];
  onRefresh?: () => Promise<void>;
}
export const ReleaseRevisions: React.FC<Props> = ({ release, revisions, onRefresh }) => {
  const dispatch = useAppDispatch();
  const [isLoadingRollbackRevision, setIsLoadingRollbackRevision] = React.useState<number | null>(null);
  const [isLoadingRollback, setIsLoadingRollback] = React.useState(false);
  const [viewingRevision, setViewingRevision] = React.useState<ReleaseRevision | null>(null);

  async function handleRollback(revision: number) {
    const gvcDisplay = release.gvc && release.gvc !== " " ? release.gvc : null;
    Modal.confirm({
      title: "Confirm Rollback",
      content: (
        <div className="flex flex-col gap-2">
          <div>Are you sure you want to rollback this release to revision {revision}?</div>
          {gvcDisplay && (
            <div>
              <span className="font-medium">GVC:</span>{" "}
              <span className="font-bold" style={{ color: "var(--color-primary)" }}>
                {gvcDisplay}
              </span>
            </div>
          )}
          <div>
            <span className="font-medium">Release Name:</span>{" "}
            <span className="font-bold" style={{ color: "var(--color-primary)" }}>
              {release.name}
            </span>
          </div>
          <div>
            <span className="font-medium">Target Revision:</span> <span className="font-bold">{revision}</span>
          </div>
        </div>
      ),
      okText: "Rollback",
      okButtonProps: { type: "primary" },
      cancelText: "Cancel",
      cancelButtonProps: { type: "default", danger: true, disabled: isLoadingRollback },
      onOk: async () => {
        try {
          setIsLoadingRollbackRevision(revision);
          setIsLoadingRollback(true);
          await request({
            service: "marketplace",
            method: "post",
            url: `/helm/rollback`,
            body: {
              org: release.org,
              name: release.name,
              revision: revision,
            },
          });
          notification.success({ message: "Success", description: "Rolled back release" });
          dispatch(addNotification({
            page: "Marketplace",
            action: "Rollback",
            success: true,
            message: `Rolled back release: ${release.name} to revision ${revision}`,
          }));
          setIsLoadingRollbackRevision(null);
          setIsLoadingRollback(false);
          if (onRefresh) {
            await onRefresh();
          }
        } catch (e) {
          setIsLoadingRollbackRevision(null);
          setIsLoadingRollback(false);
          const errorMessage = getMarketplaceErrorMessage(e);
          notification.warning({ message: "Failed", description: errorMessage });
          dispatch(addNotification({
            page: "Marketplace",
            action: "Rollback",
            success: false,
            message: `Failed to rollback release: ${release.name}`,
            details: Array.isArray(errorMessage) ? errorMessage.join("\n") : errorMessage,
          }));
          // Always refresh revisions even on failure
          if (onRefresh) {
            await onRefresh();
          }
        }
      },
    });
  }

  const releaseStateDeploymentTableItems: ReleaseRevisionTableItem[] = revisions
    .toSorted((a, b) => {
      if (a.version > b.version) return -1;
      if (b.version > a.version) return 1;
      return 0;
    })
    .map((d) => {
      return {
        ...d,
        isLoadingRollback: isLoadingRollback,
        isLoadingRollbackThis: isLoadingRollbackRevision === d.version,
      };
    });

  return (
    //
    <>
      <Table<ReleaseRevisionTableItem>
        columns={[
          {
            id: "version",
            label: "Revision",
            size: 100,
          },
          {
            id: "info.description",
            label: "Description",
          },
          {
            id: "info.status",
            label: "Status",
          },
          {
            id: "info.lastDeployed",
            label: "Updated",
            cell: ({ getValue }) => {
              return <DateString iso={getValue()} format={formatStrings.log} />;
            },
          },
          {
            id: "actions",
            label: "Actions",
            size: 180,
            cell: (p) => {
              const item = p.row.original;
              const isLatest = item.version === releaseStateDeploymentTableItems[0].version;
              const canRollback = !isLatest && ["superseded", "deployed"].includes(item.info.status);

              return (
                <div className="flex items-center gap-2">
                  <Button type="default" onClick={() => setViewingRevision(item)} size={"small"}>
                    View
                  </Button>
                  {canRollback && (
                    <Button
                      type="default"
                      onClick={() => handleRollback(item.version)}
                      size={"small"}
                      disabled={item.isLoadingRollback}
                      loading={item.isLoadingRollbackThis}
                    >
                      Rollback
                    </Button>
                  )}
                </div>
              );
            },
          },
        ]}
        data={releaseStateDeploymentTableItems}
        title={"Revisions"}
        tableId="release-revisions-update3"
        hideSettings
      />
      {viewingRevision && (
        <Modal
          open={!!viewingRevision}
          title={`Revision ${viewingRevision.version} - ${viewingRevision.secretLink || "Decoded Values"}`}
          width={"80vw"}
          styles={{ body: { height: "60vh", padding: 0 } }}
          maskClosable={false}
          onCancel={() => setViewingRevision(null)}
          footer={[
            <Button key="close" onClick={() => setViewingRevision(null)}>
              Close
            </Button>,
          ]}
        >
          <div className="h-full overflow-auto">
            <CodeEditor
              language="yaml"
              options={{
                readOnly: true,
                style: {
                  height: "100%",
                  overflow: "auto",
                },
                className: "text-sm",
              }}
              value={jsYaml.dump(
                {
                  name: viewingRevision.name,
                  version: viewingRevision.version,
                  gvc: viewingRevision.gvc,
                  chart: viewingRevision.chart,
                  config: viewingRevision.config,
                  info: viewingRevision.info,
                  valuesFiles: viewingRevision.valuesFiles,
                },
                { sortKeys: k8sKeySort },
              )}
              setValue={() => {}}
              minHeight="100%"
            />
          </div>
        </Modal>
      )}
    </>
  );
};
